// src/components/ReminderEditor.jsx
import React, { useEffect, useRef, useState } from "react";
import { MdAdd } from "react-icons/md";
import MaterialIcon from "./MaterialIcon";
import TimesField from "./TimesField";
import PatientsSelector from "./overlay_components/PatientsSelector";
import ReminderExtendedSettings from "./overlay_components/ReminderExtendedSettings";
import {
  CUSTOM_QUERY_TEMPLATE,
  QuestionFields,
  RetryFields,
  queryFormState,
  retryScheduleWarning,
  validateQuery,
} from "./queries/QueryFields";

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

const API = process.env.REACT_APP_API_URL;
const ACTIVATION_CATEGORY_PRIORITY = {
  "Gehirn jogging": 0,
  Sport: 1,
  Mindfullness: 2,
};

export default function ReminderEditor({
  patientId,
  open,
  saving,
  form,
  setForm,
  onSubmit,
  onClose,
  DOW,
  WEEKS_OF_MONTH,
  multi = false,
  isEditing = false,
  templateType = null,
}) {
  const panelRef = useRef(null);
  const [error, setError] = useState(null);

  // templates fetched from backend
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showExtendedSettings, setShowExtendedSettings] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Ensure lock fields exist (so the UI behaves even if parent didn't initialize them)
  useEffect(() => {
    if (!open) return;
    setForm((f) => {
      const tpl = f.template_id ?? "custom";
      const lockedByTpl = tpl !== "custom";

      return {
        ...f,
        template_id: tpl,
        // ✅ only default these if missing; otherwise keep what parent set
        locked_title: f.locked_title ?? lockedByTpl,
        locked_description: f.locked_description ?? lockedByTpl,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      setTemplatesLoading(true);
      try {
        // For multi overlay: generic templates endpoint
        // For single overlay: patient-specific endpoint (can include preview)
        // Query templates are only listed on request (they need query_config).
        const url = (multi
          ? `${API}/reminder-templates`
          : `${API}/reminder-templates/${patientId}`) + (templateType === "query" ? "?include_queries=true" : "");

        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          throw new Error(
            `Vorlagen konnten nicht geladen werden (GET ${url} → ${res.status}).`
          );
        }
        const payload = await res.json();
        const items = Array.isArray(payload?.items) ? payload.items : [];
        if (alive) setTemplates(items);
      } catch (e) {
        if (alive) setError(e?.message || "Vorlagen konnten nicht geladen werden.");
      } finally {
        if (alive) setTemplatesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, multi, patientId, templateType]);

  useEffect(() => {
    if (!open) {
      setShowExtendedSettings(false);
      setSelectedCategory(null);
    }
  }, [open]);

  if (!open) return null;

  const disableTemplates = isEditing;

  function toggleMonthWeek(key) {
    setForm((f) => ({
      ...f,
      month_weeks: { ...f.month_weeks, [key]: !f.month_weeks[key] },
    }));
  }
  function toggleMonthDay(key) {
    setForm((f) => ({
      ...f,
      month_days: { ...f.month_days, [key]: !f.month_days[key] },
    }));
  }

  // select a suggestion card -> set template_id + lock flags + optional defaults
  function applySuggestion(tpl) {
    if (isEditing) return; 
    setShowExtendedSettings(false);
    const defaults = tpl?.defaults ?? null;
    const fallbackDefaults = { kind: "one_off", at_local: "" };
    const templateSleepMode = tpl?.sleep_mode_after_reminder;

    const previewTitle = tpl?.preview?.title ?? null;
    const previewDesc = tpl?.preview?.description ?? null;

    const lockedTitle = tpl?.locked_title !== false; // default true
    const lockedDesc = tpl?.locked_description !== false; // default true

    setForm((f) => {
      const next = {
        ...f,
        template_id: tpl?.id ?? null,
        locked_title: lockedTitle,
        locked_description: lockedDesc,
        ...(defaults || fallbackDefaults),
      };

      if (typeof templateSleepMode === "boolean") {
        next.sleep_mode_after_reminder = templateSleepMode;
      } else if (next.sleep_mode_after_reminder == null) {
        next.sleep_mode_after_reminder = true;
      }

      // queries -> question, answers and retry from the template; the answer
      // has to be heard, so Steffi never goes to sleep right after asking
      if (tpl?.query) {
        next.query = queryFormState(tpl.query);
        next.sleep_mode_after_reminder = false;
        if (tpl.id === CUSTOM_QUERY_TEMPLATE) {
          next.title = "";
          next.description = "";
          return next;
        }
      }

      // custom template -> editable fields
      if (tpl?.id === "custom") {
        next.locked_title = false;
        next.locked_description = false;
        next.title = "";
        next.description = "";
        return next;
      }

      // locked templates -> overwrite title/description with template/preview
      if (next.locked_title) next.title = previewTitle ?? tpl?.title ?? "";
      if (next.locked_description)
        next.description = previewDesc ?? tpl?.description ?? "";

      return next;
    });
  }

  function validateForm(f) {
    if (templateType === "query") {
      if (!f?.query || !f.template_id || f.template_id === "custom") return "Bitte eine Abfrage auswählen.";
      const queryError = validateQuery(f.query, f.template_id === CUSTOM_QUERY_TEMPLATE);
      if (queryError) return queryError;
    }

    if (!f?.title?.trim()) {
      return f?.template_id && f.locked_title
        ? "Vorlagen-Titel fehlt (Template-Konfiguration prüfen)."
        : "Bitte einen Titel eingeben.";
    }

    if (f.kind === "one_off") {
      if (!f.at_local) return "Bitte einen Zeitpunkt auswählen.";
      return null;
    }

    // recurring
    if (!f.mode)
      return "Bitte einen Zeitmodus auswählen (Feste Zeiten / Zeitfenster).";

    if (f.mode === "times") {
      if (!Array.isArray(f.times) || f.times.length === 0) {
        return "Bitte mindestens eine Zeit hinzufügen.";
      }
    }

    if (f.mode === "frequency") {
      if (!f.every_minutes || !f.window_start || !f.window_end) {
        return "Bitte Zeitfenster und Intervall vollständig ausfüllen.";
      }
    }

    if (f.kind === "weekly") {
      const anyDay = Object.values(f.week_days || {}).some(Boolean);
      if (!anyDay) return "Bitte mindestens einen Wochentag auswählen.";
    }

    if (f.kind === "monthly") {
      const anyWeek = Object.values(f.month_weeks || {}).some(Boolean);
      const anyDay = Object.values(f.month_days || {}).some(Boolean);
      if (!anyWeek || !anyDay) {
        return "Bitte mindestens eine Woche und einen Wochentag auswählen.";
      }
    }

    return null;
  }

  function handleSubmit() {
    const err = validateForm(form);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onSubmit?.();
  }

  const selectedTemplateId = form?.template_id ?? "custom";
  const hasTemplateSelected = selectedTemplateId !== "custom";
  // "Eigene Abfrage" is a template whose title stays editable.
  const titleLocked = hasTemplateSelected && form?.locked_title !== false;
  const descLocked = hasTemplateSelected && form?.locked_description !== false;
  const isActivationView = templateType === "activation";
  const isQueryView = templateType === "query";
  const isCustomQuery = selectedTemplateId === CUSTOM_QUERY_TEMPLATE;
  const editorTitle = isActivationView
    ? `Aktivierung ${form?.id ? "bearbeiten" : "hinzufügen"}`
    : isQueryView
    ? `Abfrage ${isEditing ? "bearbeiten" : "erstellen"}`
    : `Erinnerung ${form?.id ? "bearbeiten" : "erstellen"}`;
  const visibleTemplates = templateType
    ? templates.filter((template) =>
        isActivationView
          ? template.template_type === "activation_routine"
          : isQueryView
          ? template.template_type === "query"
          : template.template_type !== "activation_routine" && template.template_type !== "query"
      )
    : templates;
  const selectedTemplate = visibleTemplates.find(
    (template) => template.id === selectedTemplateId
  );
  const activationCategories = isActivationView
    ? [...new Set(visibleTemplates.map((template) => template.category || "Weitere"))]
        .sort((a, b) =>
          (ACTIVATION_CATEGORY_PRIORITY[a] ?? 3) - (ACTIVATION_CATEGORY_PRIORITY[b] ?? 3)
        )
    : [];
  const activeCategory = activationCategories.includes(selectedCategory)
    ? selectedCategory
    : activationCategories[0];
  const displayedTemplates = isActivationView
    ? visibleTemplates.filter(
        (template) => (template.category || "Weitere") === activeCategory
      )
    : visibleTemplates;

  return (
    <div className="fixed inset-0 z-40">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* panel */}
      <div
        ref={panelRef}
        className={cls(
          "absolute left-1/2 z-50 -translate-x-1/2 rounded-xl border border-black/10 bg-[#e9f4ff] shadow-2xl overflow-y-auto",
          isActivationView
            ? "top-3 w-[min(1440px,96vw)] max-h-[96vh]"
            : "top-6 w-[min(1000px,92vw)] max-h-[92vh]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
          <div className="text-lg font-semibold text-[#0b1b2d]">
            {editorTitle}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="rounded-md bg-[#76B3FF] px-3 py-1.5 text-white text-sm hover:bg-[#5d9ef7] disabled:opacity-60"
            >
              {saving ? "Speichert…" : "Speichern"}
            </button>
            <button
              onClick={() => !saving && onClose?.()}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/70 disabled:opacity-60"
              disabled={saving}
            >
              Abbrechen
            </button>
          </div>
        </div>

        {/* error message */}
        {error && (
          <div className="mx-4 mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* BODY TOP: two columns */}
        <div
          className={cls(
            "grid grid-cols-1 gap-4 px-4 py-4",
            disableTemplates
              ? "md:grid-cols-1"
              : isActivationView
              ? "lg:grid-cols-[minmax(340px,0.8fr)_minmax(0,1.5fr)]"
              : "md:grid-cols-[1.1fr_1fr]"
          )}
        >
          {/* LEFT */}
          {(isActivationView || isQueryView) && !hasTemplateSelected ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center px-8 text-center text-[#657086]">
              <MdAdd className="mb-5 text-5xl text-[#8b97aa]" aria-hidden="true" />
              <p className="max-w-md text-xl leading-relaxed">
                {isQueryView
                  ? "Wähle eine Abfrage aus oder stelle mit „Eigene Abfrage“ eine eigene Frage."
                  : "Wähle eine Aktivierungsroutine aus, um Zielgruppe und Zeitplan festzulegen."}
              </p>
            </div>
          ) : (
          <div className="rounded-lg bg-white/70 p-3">
           <div className="mb-2 text-base font-semibold text-[#0b1b2d] flex items-center justify-between">
            <span>
              {isActivationView
                ? form.title || selectedTemplate?.title || selectedTemplate?.label
                : isQueryView
                ? isCustomQuery ? "Eigene Abfrage" : "Vorgeschlagene Abfrage"
                : hasTemplateSelected
                ? "Vorgeschlagene Erinnerung"
                : "Eigene Erinnerung"}
            </span>

            {titleLocked && !isActivationView && (
              <span className="text-xs text-gray-600">
                Vorlage ausgewählt (Text gesperrt)
              </span>
            )}
          </div>

            {isActivationView && (
              <p className="mb-4 text-sm leading-6 text-gray-600">
                {form.description || selectedTemplate?.description}
              </p>
            )}

            {/* only when creating “multi” reminders */}
            {multi && (
              <div className="rounded-lg bg-white/70 p-3 mb-3">
                <PatientsSelector
                  value={form.recipients}
                  onChange={(v) => setForm((f) => ({ ...f, recipients: v }))}
                />
              </div>
            )}

            <div className="grid gap-3">
              {!isActivationView && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Titel
                  </label>
                  <input
                    value={form.title || ""}
                    disabled={titleLocked}
                    onChange={(e) => {
                      setError(null);
                      setForm((f) => ({ ...f, title: e.target.value }));
                    }}
                    className={cls(
                      "w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400",
                      titleLocked
                        ? "bg-gray-100 border-gray-200 text-gray-600"
                        : "border-gray-300"
                    )}
                  />
                </div>
              )}

              {!isActivationView && !isQueryView && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beschreibung
                  </label>
                  <textarea
                    value={form.description || ""}
                    disabled={descLocked}
                    onChange={(e) => {
                      setError(null);
                      setForm((f) => ({ ...f, description: e.target.value }));
                    }}
                    rows={3}
                    className={cls(
                      "w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400",
                      descLocked
                        ? "bg-gray-100 border-gray-200 text-gray-600"
                        : "border-gray-300"
                    )}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={(e) => {
                        setError(null);
                        setForm((f) => ({ ...f, is_active: e.target.checked }));
                      }}
                    />
                    Aktiv
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Typ
                </label>
                <select
                  value={form.kind}
                  onChange={(e) => {
                    setError(null);
                    setForm((f) => ({ ...f, kind: e.target.value }));
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="one_off">Einmalig</option>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                </select>
              </div>

              {/* One-off */}
              {form.kind === "one_off" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Zeitpunkt
                  </label>
                  <input
                    type="datetime-local"
                    value={form.at_local || ""}
                    onChange={(e) => {
                      setError(null);
                      setForm((f) => ({ ...f, at_local: e.target.value }));
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}

              {!isActivationView && !isQueryView && (
                <div className="mt-3">
                  <ReminderExtendedSettings
                    expanded={showExtendedSettings}
                    onToggle={() => setShowExtendedSettings((v) => !v)}
                    sleepModeAfterReminder={form.sleep_mode_after_reminder !== false}
                    onSleepModeAfterReminderChange={(value) =>
                      setForm((f) => ({ ...f, sleep_mode_after_reminder: value }))
                    }
                  />
                </div>
              )}
            </div>
          </div>
          )}

          {/* RIGHT: template cards */}
          {!disableTemplates && (
          <div
            className="rounded-lg bg-white/70 p-3"
          >
            <div className="mb-2 text-base font-semibold text-[#0b1b2d] flex items-center justify-between">
              <span>
                {isActivationView
                  ? "Aktivierungsroutinen"
                  : isQueryView
                  ? "Abfragen"
                  : "Vorgeschlagene Erinnerung"}
              </span>
              {templatesLoading && (
                <span className="text-xs text-gray-700">Lade Vorlagen…</span>
              )}
            </div>

            {isActivationView && (
              <div className="mb-4 flex flex-wrap gap-2">
                {activationCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    aria-pressed={activeCategory === category}
                    onClick={() => setSelectedCategory(category)}
                    className={cls(
                      "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors",
                      activeCategory === category
                        ? "border-[#76B3FF] bg-[#76B3FF] text-white shadow-sm"
                        : "border-blue-200 bg-white text-[#42526a] hover:bg-blue-50"
                    )}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            <div className={cls("overflow-y-auto pr-1", isActivationView ? "max-h-[62vh]" : "max-h-[340px]")}>
              <div
                className={cls(
                  "grid gap-3",
                  isActivationView
                    ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4"
                    : "grid-cols-3"
                )}
              >
                {displayedTemplates.map((tpl) => {
                  const selected = (form.template_id ?? null) === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      disabled={saving || templatesLoading}
                      onClick={() => {
                        setError(null);
                        applySuggestion(tpl);
                      }}
                      className={cls(
                        "relative rounded-lg border bg-white p-3 text-left shadow-sm hover:bg-gray-50 disabled:opacity-60",
                        selected
                          ? "border-blue-400 ring-2 ring-blue-200"
                          : "border-gray-200"
                      )}
                      title={tpl.label}
                    >
                      <span
                        className={cls(
                          "absolute right-2 top-2 h-4 w-4 rounded-full border",
                          selected
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300 bg-white"
                        )}
                        aria-hidden="true"
                      />

                      <div className="flex flex-col items-center gap-2">
                        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                          {isActivationView || isQueryView ? (
                            <MaterialIcon
                              name={tpl.icon_name}
                              className="h-7 w-7 text-[#347bdc]"
                            />
                          ) : (
                            <img
                              src={tpl.image}
                              alt=""
                              className="h-10 w-10 object-contain"
                            />
                          )}
                        </div>
                        <div className="text-center text-xs font-medium leading-tight text-gray-800">
                          {tpl.label}
                        </div>
                        {(tpl.id === "custom" || tpl.id === CUSTOM_QUERY_TEMPLATE) && (
                          <div className="text-[10px] text-gray-500">
                            editierbar
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* QUERY: question and answers */}
        {isQueryView && hasTemplateSelected && form.query && (
          <div className="px-4 pb-4">
            <div className="rounded-lg bg-white/70 p-3">
              <div className="mb-2 text-base font-semibold text-[#0b1b2d]">
                Frage & Antworten
              </div>
              <QuestionFields
                query={form.query}
                custom={isCustomQuery}
                onChange={(query) => {
                  setError(null);
                  setForm((f) => ({ ...f, query }));
                }}
              />
            </div>
          </div>
        )}

        {/* BOTTOM: scheduling (ONLY if not one_off) */}
        {form.kind !== "one_off" && (
          <div className="px-4 pb-4">
            <div className="rounded-lg bg-white/70 p-3">
              <div className="mb-2 text-base font-semibold text-[#0b1b2d]">
                Zeit & Wiederholung
              </div>

              <div className="grid gap-3">
                {/* DAILY */}
                {form.kind === "daily" && (
                  <>
                    <div className="flex gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "times"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "times" }));
                          }}
                        />
                        Feste Zeiten
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "frequency"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "frequency" }));
                          }}
                        />
                        Zeitfenster
                      </label>
                    </div>

                    {form.mode === "times" ? (
                      <TimesField
                        label="Zeiten"
                        value={form.times}
                        onChange={(arr) => {
                          setError(null);
                          setForm((f) => ({ ...f, times: arr }));
                        }}
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Alle X Minuten
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={form.every_minutes}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                every_minutes: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Start
                          </label>
                          <input
                            type="time"
                            value={form.window_start}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_start: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Ende
                          </label>
                          <input
                            type="time"
                            value={form.window_end}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_end: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* WEEKLY */}
                {form.kind === "weekly" && (
                  <>
                    <div className="mb-1 text-sm font-medium text-gray-700">
                      Wochentage
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {DOW.map((d) => (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => {
                            setError(null);
                            setForm((f) => ({
                              ...f,
                              week_days: {
                                ...f.week_days,
                                [d.key]: !f.week_days[d.key],
                              },
                            }));
                          }}
                          className={cls(
                            "px-2 py-1 rounded border text-sm",
                            form.week_days?.[d.key]
                              ? "bg-blue-100 border-blue-400"
                              : "bg-white border-gray-300"
                          )}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-3 text-sm mt-2">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "times"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "times" }));
                          }}
                        />
                        Feste Zeiten
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "frequency"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "frequency" }));
                          }}
                        />
                        Zeitfenster
                      </label>
                    </div>

                    {form.mode === "times" ? (
                      <TimesField
                        label="Zeiten"
                        value={form.times}
                        onChange={(arr) => {
                          setError(null);
                          setForm((f) => ({ ...f, times: arr }));
                        }}
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Alle X Minuten
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={form.every_minutes}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                every_minutes: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Start
                          </label>
                          <input
                            type="time"
                            value={form.window_start}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_start: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Ende
                          </label>
                          <input
                            type="time"
                            value={form.window_end}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_end: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* MONTHLY */}
                {form.kind === "monthly" && (
                  <>
                    <div className="mb-1 text-sm font-medium text-gray-700">
                      Wochen im Monat
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {WEEKS_OF_MONTH.map((w) => (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => {
                            setError(null);
                            toggleMonthWeek(String(w.key));
                          }}
                          className={cls(
                            "px-2 py-1 rounded border text-sm",
                            form.month_weeks?.[String(w.key)]
                              ? "bg-blue-100 border-blue-400"
                              : "bg-white border-gray-300"
                          )}
                        >
                          {w.label}
                        </button>
                      ))}
                    </div>

                    <div className="mb-1 mt-2 text-sm font-medium text-gray-700">
                      Tage
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {DOW.map((d) => (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => {
                            setError(null);
                            toggleMonthDay(d.key);
                          }}
                          className={cls(
                            "px-2 py-1 rounded border text-sm",
                            form.month_days?.[d.key]
                              ? "bg-blue-100 border-blue-400"
                              : "bg-white border-gray-300"
                          )}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-3 text-sm mt-2">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "times"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "times" }));
                          }}
                        />
                        Feste Zeiten
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={form.mode === "frequency"}
                          onChange={() => {
                            setError(null);
                            setForm((f) => ({ ...f, mode: "frequency" }));
                          }}
                        />
                        Zeitfenster
                      </label>
                    </div>

                    {form.mode === "times" ? (
                      <TimesField
                        label="Zeiten"
                        value={form.times}
                        onChange={(arr) => {
                          setError(null);
                          setForm((f) => ({ ...f, times: arr }));
                        }}
                      />
                    ) : (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Alle X Minuten
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={form.every_minutes}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                every_minutes: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Start
                          </label>
                          <input
                            type="time"
                            value={form.window_start}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_start: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Fenster Ende
                          </label>
                          <input
                            type="time"
                            value={form.window_end}
                            onChange={(e) => {
                              setError(null);
                              setForm((f) => ({
                                ...f,
                                window_end: e.target.value,
                              }));
                            }}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* QUERY: asking again when nobody answered */}
        {isQueryView && hasTemplateSelected && form.query && (
          <div className="px-4 pb-4">
            <div className="rounded-lg bg-white/70 p-3">
              <div className="mb-2 text-base font-semibold text-[#0b1b2d]">
                Wenn keine Antwort kommt
              </div>
              <RetryFields
                query={form.query}
                warning={retryScheduleWarning(form)}
                onChange={(query) => {
                  setError(null);
                  setForm((f) => ({ ...f, query }));
                }}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
